import EventHandler from '../../../../utils/dom/event-handler';
import SearchInputField from '../../../../components/SearchInputField';
import ParkFinder from '../../../../components/ParkFinder';
import SelectorEngine from '../../../../utils/dom/selector-engine';

const CLICK_EVENT = 'click';
const HIDDEN_CLASS = 'u-hidden';

/**
 * Venue Registration search finder.
 * Extends ParkFinder so shared parkFinder / coachVenueSearch / activatorVenueSearch
 * questions remain unchanged.
 */
class VenueRegistrationVenueFinder extends ParkFinder {
	afterRender() {
		super.afterRender();

		// Venue Registration's manual entry form has no address look-up alternative,
		// so hide the shared ParkFinder "Use venue look-up search" button.
		this.parkFinderAddressButton?.classList.add(HIDDEN_CLASS);
	}

	initSearchInputField() {
		this.searchInputTextField = new SearchInputField(this.parkFinderSearchInput, {
			data: this.data
		});
		this.searchInputTextField.dataMapping = (item) => {
			return {
				firstLineText: item.venueName,
				secondLineText: `${item.county}, ${item.postcode}`,
				dataKey: this.data.dataKey,
				dataValue: item.id
			};
		};
		this.searchInputTextField.filterDataMapping = (item, event) => {
			return item.id === event[this.data.dataKey];
		};
		this.searchInputTextField.searchInputMappingChange();
		this.searchInputTextField.onChange((event) => {
			this.loadParkResult(event.value, true, true);
		});
		this.setupValidateObject();
		if (this.data?.value) {
			this.loadParkResult(this.data?.value, false, false);
		}
	}

	async runOnSelectHandler(selectedItem = {}) {
		if (typeof this.data?.onSelectHandler !== 'function') {
			return selectedItem;
		}

		try {
			const result = await this.data.onSelectHandler(selectedItem);
			return result || selectedItem;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('VenueRegistrationVenueFinder: onSelectHandler failed', error);
			return selectedItem;
		}
	}

	async loadParkResult(resultDetails, isSetupValueObject, shouldRunSelectHandler = false) {
		let resolvedDetails = resultDetails;

		if (shouldRunSelectHandler) {
			resolvedDetails = await this.runOnSelectHandler(resultDetails);
		}

		const hasWebAdminError = !!resolvedDetails?.isAdmin;

		if (isSetupValueObject) {
			if (hasWebAdminError) {
				this.value = undefined;
			} else {
				this.setupValueObject(resolvedDetails, 'Complete');
			}
		}

		if (!resolvedDetails?.venueName) {
			return;
		}

		this.parkFinderSearchInputWrapper.classList.add(HIDDEN_CLASS);
		this.parkFinderSearchForm.classList.add(HIDDEN_CLASS);
		this.parkFinderTitle.classList.remove(HIDDEN_CLASS);
		this.parkFinderSearchResult.classList.remove(HIDDEN_CLASS);
		this.parkFinderSearchResult.innerHTML = '';
		this.parkFinderSearchResult.insertAdjacentHTML(
			'beforeend',
			this.renderAutoSuggestResult(resolvedDetails, hasWebAdminError)
		);
		this.afterAutoSuggestResultRender();
		this.setSubmitDisabled(hasWebAdminError);
	}

	afterAutoSuggestResultRender() {
		const suggestionAutoSuggestResult = SelectorEngine.findOne(
			'.lta-autosuggest-input-result',
			this.parkFinderSearchResult
		);
		const searchAgainButton = SelectorEngine.findOne('.lta-park-search--again', suggestionAutoSuggestResult);

		EventHandler.on(searchAgainButton, CLICK_EVENT, () => {
			this.value = undefined;
			suggestionAutoSuggestResult.remove();
			this.searchInputTextField.remove();
			this.initSearchInputField();
			this.parkFinderSearchInputWrapper.classList.remove(HIDDEN_CLASS);
			this.parkFinderTitle.classList.add(HIDDEN_CLASS);
			this.parkFinderSearchForm.classList.add(HIDDEN_CLASS);
			this.parkFinderSearchResult.classList.add(HIDDEN_CLASS);
			this.setSubmitDisabled(false);
		});
	}

	setSubmitDisabled(isDisabled) {
		if (typeof this.data?.onSubmitDisabledChange === 'function') {
			this.data.onSubmitDisabledChange(isDisabled);
		}
	}

	renderAutoSuggestResult(parkDetail, hasWebAdminError = false) {
		const searchAgainLabel = this.data?.searchAgainLabel || 'Search again';
		const searchAgainIcon = this.data?.searchAgainIcon || 'search';

		return `<div class="lta-autosuggest-input-result u-mb-4">
					<div class="u-mb-4">
						${this.renderInstructionLabel(this.data.instructionText)}
					</div>
					${!this.data?.resultTitleNotVisible ? `<div class="lta-text-input__label">${this.data.label || 'Venue name'}</div>` : ''}
					<div class="u-px-3 u-py-2 u-bg-background-blue u-rounded">
						<div class="lta-autosuggest-input-result_park-name u-h6 u-mb-1">${parkDetail.venueName}</div>
						<div class="u-body2 u-mb-2">
							<br>${parkDetail.county}
							<br>${parkDetail.postcode}
						</div>
						<button type="button" class="lta-btn lta-btn--xs lta-btn--petite lta-btn--icon lta-btn--icon-before lta-park-search--again">
							<svg class="lta-icon js-lta-text-input--btn-icon u-mr-1" width="1.5em" height="1.5em" fill="currentColor" alt="${searchAgainIcon}" aria-hidden="true">
								<use xlink:href="/static/images/icons/lta-icons.svg#${searchAgainIcon}"></use>
							</svg>${searchAgainLabel}
						</button>
					</div>
					${hasWebAdminError ? this.renderWebAdminErrorSnackbar() : ''}
				</div>`;
	}

	renderWebAdminErrorSnackbar() {
		const supportLabel = this.data?.webAdminSupportLinkLabel || 'LTA Customer Support team';
		const supportUrl = this.data?.webAdminSupportLinkUrl || 'https://www.lta.org.uk/about-us/what-we-do/contact-us/';
		const message =
			this.data?.webAdminErrorMessage ||
			`This organisation already has a Web Admin assigned. Please contact them directly to request access. If you are unsure who the Web Admin is or have any further questions, please contact the <a href="${supportUrl}" class="u-link u-text-lta-blue" target="_blank" rel="noopener noreferrer">${supportLabel}</a>.`;

		return `
			<div class="lta-venue-selection-search__web-admin-error u-mt-3" role="alert">
				<div class="lta-snackbar lta-snackbar--alert">
					<ul>
						<li>
							<span>${message}</span>
						</li>
					</ul>
				</div>
			</div>
		`;
	}
}

export default VenueRegistrationVenueFinder;
